import { FetchClient, RequestArgs, RequestFailure, RequestResult } from '@ff-it/api';
import { auth } from './auth';
import { getConnectionId } from './realtime/connectionRef';
import { Button, DialogBody, DialogContent, DialogFooter, DialogHeader, openModal } from '@ff-it/ui';
import { SubmissionErrors } from 'final-form';
import { getDRFFormError } from '@ff-it/form';

const headers: Record<string, string> = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

const getCSRFToken = () => {
  const document = window.document;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    const name = 'csrftoken';
    for (let i = 0; i < cookies.length; i++) {
      const cookie = String(cookies[i]).trim();
      if (cookie.substring(0, name.length + 1) === name + '=') {
        return window.decodeURIComponent(cookie.substring(name.length + 1));
      }
    }
  }
  return null;
};

let reloading = false;
function reloadStaleClient(res: RequestResult<unknown, any>): void {
  const clientVersion = CLIENT_VERSION;
  if (!clientVersion || reloading) {
    return;
  }

  const serverVersion = res.response?.headers.get('X-Server-Version');

  if (serverVersion) {
    if (serverVersion !== clientVersion) {
      reloading = true;
      openModal(
        ({ onSubmit }) => (
          <DialogContent>
            <DialogHeader title="Update Available!" />
            <DialogBody>
              Version <strong data-testid="server-version">{serverVersion}</strong> is now ready.
              <br />
              Please reload to upgrade.
            </DialogBody>
            <DialogFooter>
              <Button ml="auto" onClick={onSubmit}>
                Reload
              </Button>
            </DialogFooter>
          </DialogContent>
        ),
        { testId: 'reload-stale-client', dismissProps: { enabled: false }, scope: 'ROOT' },
      ).then(() => {
        reloading = false;
        window.location.reload();
      });
    }
  }
}

export const api = new FetchClient({
  baseURL: '/api/v1',
  headers,
  credentials: 'same-origin',
  before: (r) => {
    r.headers.set('X-Client-Version', CLIENT_VERSION);

    // Add CSRF token for non-GET requests
    if (r.method !== 'GET') {
      const csrfToken = getCSRFToken();
      if (csrfToken) {
        r.headers.set('X-CSRFToken', csrfToken);
      }
    }

    const connectionId = getConnectionId();
    if (connectionId) {
      r.headers.set('X-Realtime-Connection', connectionId);
    }

    const { scope, impersonation } = auth.getSnapshot();

    const enterpriseId = scope?.kind === 'AGENCY' ? scope.id : undefined;
    const impersonationId = impersonation?.id;

    if (enterpriseId) {
      r.headers.set('X-Enterprise-Id', enterpriseId.toString());
    }

    if (impersonationId) {
      r.headers.set('X-Impersonation-Id', impersonationId.toString());
    }
  },
  after: (res: RequestResult<unknown, any>) => {
    if (res.status === 401) {
      auth.signout(() => {
        window.location.replace('/login');
      });
    } else if (res.status === 403) {
      if (res.error && res.data.detail && typeof res.data.detail === 'string') {
        res.error.message = res.data.detail;
      }
    }
    reloadStaleClient(res);
  },
});

export const fetcher = <R,>(args: RequestArgs): Promise<R> =>
  api.request<R, unknown>(args).then((res) => {
    if (res.ok) {
      return res.data;
    }
    // @FIXME: call fetch directly or have ane error types
    // error has no request data and we can't recorver from that so we just raise failure :<
    throw res;
  });

export const swrOptions = {
  fetcher,
  revalidateOnFocus: false,
} as const;

export type Fetcher = <R>(args: RequestArgs) => Promise<R>;

// This is closer to what RequestResult should be
export class FetchResult<T> {
  private constructor(
    private readonly _value: T | null,
    private readonly _error: RequestFailure<any> | null,
  ) {}

  static ok<T>(value: T): FetchResult<T> {
    return new FetchResult<T>(value, null);
  }

  assertOk(): void {
    if (this._error) {
      throw this._error;
    }
  }

  static err<T>(error: any): FetchResult<T> {
    return new FetchResult<T>(null, error);
  }

  isOk(): boolean {
    return this._error === null;
  }

  unwrap(): T {
    if (this._error) {
      throw this._error;
    }
    return this._value!;
  }

  // valueOrNull(): T | null {
  //   return this._value;
  // }

  // intoFormError(): SubmissionErrors | null {
  //   if (!this._error) return null;
  //   return getDRFFormError(this._error) as SubmissionErrors | null;
  // }

  unwrapFormError(): SubmissionErrors | void {
    if (!this._error) return;
    const err = getDRFFormError(this._error) as SubmissionErrors | null;
    if (err) {
      return err;
    }
    throw this._error;
  }

  /**
   * Transform the data if successful.
   */
  map<U>(fn: (data: T) => U): FetchResult<U> {
    if (this._error) {
      return FetchResult.err<U>(this._error);
    }
    try {
      return FetchResult.ok<U>(fn(this._value!));
    } catch (e) {
      // Transformation failed
      return FetchResult.err<U>(e);
    }
  }

  /**
   * Run a side effect if successful
   */
  tap(fn: (data: T) => void): FetchResult<T> {
    if (this.isOk()) {
      fn(this._value!);
    }
    return this;
  }
}
